import React, { useEffect, useRef } from 'react';
import img1 from '../../assets/skyford_excellence.png';
import LazyImage from '../common/LazyImage';

const Facilities = () => {
  const facilities = [
    {
      id: '01',
      title: 'Digital Classrooms',
      description: 'State-of-the-art digital learning environments equipped with modern technology',
      icon: '💻'
    },
    {
      id: '02',
      title: 'Well Equipped Seminar Halls',
      description: 'Spacious halls with modern presentation equipment and comfortable seating',
      icon: '🎯'
    },
    {
      id: '03',
      title: 'Wi-Fi Enabled Campus',
      description: 'High-speed internet connectivity throughout the entire campus',
      icon: '📡'
    },
    {
      id: '04',
      title: 'Air Conditioned Classrooms with Projector',
      description: 'Climate-controlled environments with modern projectors for enhanced learning',
      icon: '❄️'
    },
    {
      id: '05',
      title: 'PCs and Audio-Video Facilities',
      description: 'Advanced computer labs and multimedia equipment for practical training',
      icon: '🖥️'
    }
  ];

  const cardsRef = useRef([]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
          observer.unobserve(entry.target); // Stop observing once animation is triggered
        }
      });
    }, observerOptions);

    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
      
    <div className="mt-8 sm:mt-14 min-h-screen py-16 px-6">
      <style>
        {`
          .facility-card {
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease-out, transform 0.8s ease-out;
          }
          
          .facility-card.show {
            opacity: 1;
            transform: translateY(0);
          }
          
          .facility-card:nth-child(2).show { transition-delay: 0.1s; }
          .facility-card:nth-child(3).show { transition-delay: 0.2s; }
          .facility-card:nth-child(4).show { transition-delay: 0.3s; }
          .facility-card:nth-child(5).show { transition-delay: 0.4s; }
        `}
      </style>

      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="mx-0 mb-16 items-center grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="inline-block">
              <div className="h-1 w-24 bg-red-600 mb-2"></div>
              <span className="text-red-600 font-semibold tracking-wider">EXCELLENCE IN AVIATION</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight">
              Shaping Future 
              <span className="text-red-600"> Aviation </span> 
              Leaders
            </h1>
            <p className="text-lg text-gray-700">
              Skyford Aviation Academy is equipped with modern facilities, including a well equipped seminar halls, digital classrooms, Wi-Fi enabled campus and a well-stocked library. Students also have access to experienced instructors and industry experts who provide industry insights and guidance.
            </p>
          </div>
          <div className="relative">
            <LazyImage 
              src={img1} 
              alt="Facilities Excellence" 
              className="m-auto bg-red-500 rounded-t-full"
            />
          </div>
        </div>

        {/* Facilities Grid */}
        <div className="">
          <h2 className="text-4xl font-medium text-center mb-6 sm:mb-8 mt-20 sm:mt-32">Our Facilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {facilities.map((facility, index) => (
              <div
                key={facility.id}
                ref={el => cardsRef.current[index] = el}
                className="facility-card bg-white p-6 rounded-xl group"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center text-2xl group-hover:bg-red-600 hover:text-white transition-colors duration-300">
                    {facility.icon}
                  </div>
                  <span className="text-lg font-medium text-red-600">{facility.title}</span>
                </div>
                <p className="text-gray-600 text-sm">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Facilities;